import { useState } from 'react'
import type { GridPinnedRowsProp } from '@microservices/wiskey-react-table-component/dist/DataGrid/DataGridV7/hooks/features/rowPinning'

import { getDynamicColumnFieldKey } from '@components/TimeLineGrid/helpers/getDynamicColumnFieldKey'

import { useExecuteCommandMutation } from '@redux/api'
import {
  initializeDynamicColumnsTotals,
  setDynamicColumnsIsLoading,
  setDynamicColumnsTotalData,
} from '@redux/reducers/timeLine.reducer'

import { useAppDispatch } from '@hooks'
import { getCommandRequestData, getRSqlBySearch, isObjectValueType } from '@helpers'
import { BOTTOM_PINNED_TOTAL_ROW_ID, TIME_LINE_MODE } from '@constants'
import {
  DateIntervalKey,
  GETCommand,
  ObjectDataRecord,
  POSTObjectDataFilter,
  TimeLineDTOType,
  TimeLineGridDataRecord,
} from '@types'

import { getDateIntervalColumnFieldKey } from '../helpers'
import { DateInterval } from '../helpers/splitIntervalByPeriod'

import { useHandlers } from './useHandlers'

type UseGetTotalsType = {
  config: TimeLineDTOType
} & ReturnType<typeof useHandlers>

export type RefreshTotalsParams = {
  objectYSAFiltersCriteria?: POSTObjectDataFilter
  objectXSAFiltersCriteria?: POSTObjectDataFilter
  objectXColumnsObjectSAFiltersCriteria?: POSTObjectDataFilter
  dateIntervals?: DateInterval[]
  columnsObjectData?: ObjectDataRecord[]
}

export const useGetTotals = (props: UseGetTotalsType) => {
  const {
    config,
    state: {
      dateIntervals: stateDateIntervals,
      objectYSAFiltersCriteria: stateObjectYSAFiltersCriteria,
      objectXSAFiltersCriteria: stateObjectXSAFiltersCriteria,
      objectXColumnsObjectSAFiltersCriteria: stateObjectXColumnsObjectSAFiltersCriteria,
    },
  } = props

  const dispatch = useAppDispatch()

  const [pinnedRows, setPinnedRows] = useState<GridPinnedRowsProp>({})

  const totalsRows = config.objectX.totalsRows

  const [executeCommand] = useExecuteCommandMutation()

  const getColumnTotals = (data: {
    rowIndex: number
    key: string
    srcObj: ObjectDataRecord
    objectYSAFiltersCriteria?: POSTObjectDataFilter
    objectXSAFiltersCriteria?: POSTObjectDataFilter
    objectXColumnsObjectSAFiltersCriteria?: POSTObjectDataFilter
  }) => {
    const {
      key,
      srcObj,
      rowIndex,
      objectYSAFiltersCriteria = stateObjectYSAFiltersCriteria,
      objectXSAFiltersCriteria = stateObjectXSAFiltersCriteria,
      objectXColumnsObjectSAFiltersCriteria = stateObjectXColumnsObjectSAFiltersCriteria,
    } = data

    const { commands } = totalsRows[rowIndex]

    if (!commands?.length) {
      return
    }

    dispatch(
      setDynamicColumnsIsLoading({
        rowIndex,
        key,
        isLoading: true,
      })
    )

    const promises = commands.map(command =>
      getTotalsFromCommand({
        command,
        srcObj,
        objectYSAFiltersCriteria,
        objectXSAFiltersCriteria,
        objectXColumnsObjectSAFiltersCriteria,
      })
    )

    Promise.allSettled(promises).then(data => {
      dispatch(
        setDynamicColumnsTotalData({
          rowIndex,
          key,
          value: data.reduce((acc, currentValue, currentIndex) => {
            if (currentValue.status === 'fulfilled') {
              return {
                ...acc,
                [`commandData${currentIndex + 1}`]: currentValue.value,
              }
            }

            return {
              ...acc,
            }
          }, {}),
        })
      )
    })
  }

  const getTotalsFromCommand = async (data: {
    command: GETCommand
    srcObj: ObjectDataRecord
    objectYSAFiltersCriteria?: POSTObjectDataFilter
    objectXSAFiltersCriteria?: POSTObjectDataFilter
    objectXColumnsObjectSAFiltersCriteria?: POSTObjectDataFilter
  }) => {
    const { command, srcObj } = data

    const parameters = command?.parameters
      ? getCommandRequestData(command.parameters, srcObj)
      : undefined

    const filter = [parameters?.filter].filter(item => item).join(';')

    const commandProps = {
      objectCode: command?.objectCode as string,
      commandCode: command?.name as string,
      params:
        command?.parameters || filter
          ? {
              ...(parameters || {}),
              filter,
            }
          : undefined,
      body:
        command?.body && command?.httpMethod !== 'GET'
          ? getCommandRequestData(command.body, srcObj)
          : undefined,
      method: command?.httpMethod as string,
    }

    return executeCommand(commandProps).unwrap()
  }

  const refreshTotals = (params: RefreshTotalsParams) => {
    const {
      dateIntervals = stateDateIntervals,
      columnsObjectData,
      objectYSAFiltersCriteria,
      objectXSAFiltersCriteria,
      objectXColumnsObjectSAFiltersCriteria,
    } = params

    if (!totalsRows?.length) {
      return
    }

    if (config.mode === TIME_LINE_MODE.OBJECT && !columnsObjectData?.length) {
      return
    }

    const result: TimeLineGridDataRecord[] = []

    totalsRows.forEach((row, index) => {
      result[index] = {
        id: `${BOTTOM_PINNED_TOTAL_ROW_ID}_${row.id}`,
        title: row.title,
        yElement: {},
        columnsInfo: {},
      }

      if (config.mode === TIME_LINE_MODE.DATE_TIME) {
        dateIntervals.forEach(interval => {
          const dateIntervalKey = getDateIntervalColumnFieldKey(
            interval.startDate,
            interval.endDate
          ) as keyof DateIntervalKey

          result[index][dateIntervalKey] = []
        })
      }

      if (config.mode === TIME_LINE_MODE.OBJECT && columnsObjectData) {
        columnsObjectData.forEach(columnObject => {
          const key = getDynamicColumnFieldKey(
            columnObject,
            config.objectX.fieldFromColumnsObjectToLink
          )

          result[index][key] = []
        })
      }
    })

    dispatch(initializeDynamicColumnsTotals(result))

    setPinnedRows({ bottom: result })

    totalsRows.forEach((_, index) => {
      if (config.mode === TIME_LINE_MODE.DATE_TIME) {
        dateIntervals.forEach(interval => {
          getColumnTotals({
            key: getDateIntervalColumnFieldKey(interval.startDate, interval.endDate),
            rowIndex: index,
            srcObj: {
              startDate: interval.startDate,
              endDate: interval.endDate,
              columnInfo: { startDate: interval.startDate, endDate: interval.endDate },
            },
            objectYSAFiltersCriteria,
            objectXSAFiltersCriteria,
          })
        })
      }

      if (config.mode === TIME_LINE_MODE.OBJECT && columnsObjectData) {
        columnsObjectData.forEach(columnObject => {
          getColumnTotals({
            key: getDynamicColumnFieldKey(
              columnObject,
              config.objectX.fieldFromColumnsObjectToLink
            ),
            rowIndex: index,
            srcObj: { columnInfo: columnObject },
            objectYSAFiltersCriteria,
            objectXSAFiltersCriteria,
            objectXColumnsObjectSAFiltersCriteria,
          })
        })
      }
    })
  }

  return {
    pinnedRows,
    getColumnTotals,
    refreshTotals,
  }
}
